/**
 * OfficeCleanerHeaderMobile — redesigned mobile header for the office_cleaner.
 *
 * Same family as the hourly/commissioned headers, with a NIGHT-shift dial
 * (window 20:00 -> 06:00, wraps midnight) and a "Today's tasks" progress bar
 * (checklist done/total) in place of the production bar. Ported from the
 * Claude-Design handoff (Turn 1). Three states: not clocked in / on shift /
 * done. Clock in-out use the shared Header handlers (no billing regression —
 * extra services are still confirmed from the My Day list below).
 */

import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { SignOut, UserCircle, SignIn, Moon, SunHorizon, Check } from '@phosphor-icons/react';
import NotificationBell from './NotificationBell';

const BLUE = '#2563eb', TEAL = '#4A7A91';
const pad2 = (n) => String(n).padStart(2, '0');
const fmtHM = (iso) => { const d = new Date(iso); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtElapsed = (s) => `${pad2(Math.floor(s / 3600))}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(s % 60)}`;

// Night window 20:00->06:00 (10h) mapped onto the 180° semicircle.
function nightXY(h) {
  let f = ((h - 20 + 24) % 24) / 10;
  f = Math.max(0, Math.min(1, f));
  const th = f * Math.PI;
  return { x: 90 - 70 * Math.cos(th), y: 100 - 70 * Math.sin(th) };
}
const hourOf = (iso) => { const d = new Date(iso); return d.getHours() + d.getMinutes() / 60; };

export default function OfficeCleanerHeaderMobile({
  user, clockEvent, elapsed, isClockedIn, isClockedOut, clockLoading,
  onClockIn, onClockOut, onLogout, onAccount,
}) {
  const [tasksTotal, setTasksTotal] = useState(0);

  useEffect(() => {
    api.getChecklistTasks().then(t => setTasksTotal(Array.isArray(t) ? t.length : 0)).catch(() => {});
  }, []);

  const hasShift = isClockedIn || isClockedOut;
  const startH = hasShift && clockEvent?.clock_in_at ? hourOf(clockEvent.clock_in_at) : null;
  const endH   = isClockedOut && clockEvent?.clock_out_at ? hourOf(clockEvent.clock_out_at) : null;
  const s = startH != null ? nightXY(startH) : null;
  const now = new Date();
  const nowXY = isClockedIn ? nightXY(now.getHours() + now.getMinutes() / 60) : null;
  const e = isClockedOut && endH != null ? nightXY(endH) : nowXY;
  const arcD = s && e ? `M${s.x.toFixed(1)},${s.y.toFixed(1)} A70,70 0 0 1 ${e.x.toFixed(1)},${e.y.toFixed(1)}` : null;

  const centerLabel = isClockedIn ? 'ON SHIFT' : isClockedOut ? 'TOTAL' : 'NOT CLOCKED IN';
  const centerValue = isClockedIn ? fmtElapsed(elapsed || 0)
    : isClockedOut ? `${(clockEvent?.hours_worked || 0).toFixed(2)}h` : '--:--';
  const centerColor = hasShift ? BLUE : '#9ca3af';

  // Checklist progress
  const done = Array.isArray(clockEvent?.checklist) ? clockEvent.checklist.length : 0;
  const total = tasksTotal;
  const taskPct = total > 0 ? Math.round((done / total) * 100) : 0;
  const tasksFull = total > 0 && done >= total;

  const iconBtn = { position: 'relative', background: '#f3f4f6', border: 'none', width: 38, height: 38, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };

  return (
    <header className="mobile-oc-header" style={{ background: '#fff', padding: '6px 16px 16px', borderBottom: '1px solid #eef0f2', position: 'sticky', top: 0, zIndex: 100 }}>
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <img src={process.env.PUBLIC_URL + '/ACTS_CAR_DETAILING.png'} alt="Acts" style={{ height: 46, objectFit: 'contain' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NotificationBell
            onAccount={onAccount}
            extra={!isClockedIn && !isClockedOut
              ? [{ id: 'clockin', kind: 'clockin', title: 'Clock in', body: "You haven't clocked in tonight yet.", actionLabel: 'Clock in', onAction: onClockIn }]
              : []}
          />
          <button onClick={onAccount} style={{ ...iconBtn, color: '#1e3a4a' }} title="My Account" aria-label="My Account"><UserCircle size={20} weight="duotone" /></button>
          <button onClick={onLogout} style={{ ...iconBtn, color: '#374151' }} title="Logout" aria-label="Logout"><SignOut size={19} /></button>
        </div>
      </div>

      {/* Night dial + side info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <svg width="180" height="112" viewBox="0 0 180 112" style={{ flex: 'none' }}>
          <path d="M20,100 A70,70 0 0 1 160,100" fill="none" stroke="#eceef0" strokeWidth="16" />
          <path d="M20,100 A70,70 0 0 1 160,100" fill="none" stroke="#f8f9fa" strokeWidth="10" strokeDasharray="1 3.6" />
          {arcD && <>
            <path d={arcD} fill="none" stroke={BLUE} strokeWidth="16" strokeLinecap="round" />
            <path d={arcD} fill="none" stroke="rgba(255,255,255,.35)" strokeWidth="11" strokeDasharray="1.4 4" />
          </>}
          {isClockedIn && nowXY && (
            <g transform={`translate(${nowXY.x.toFixed(1)},${nowXY.y.toFixed(1)})`} style={{ animation: 'ocPulse 1.8s ease-in-out infinite' }}>
              <circle r="6.5" fill="#fff" /><circle r="4" fill={BLUE} />
            </g>
          )}
          {/* start knob */}
          {s ? (<><circle cx={s.x} cy={s.y} r="8" fill={BLUE} /><circle cx={s.x} cy={s.y} r="2.6" fill="#fff" /></>)
             : (<><circle cx="20" cy="100" r="8" fill="#d1d5db" /><circle cx="20" cy="100" r="2.6" fill="#fff" /></>)}
          {/* end knob: done → blue at clock-out; else grey at 06:00 window end */}
          {isClockedOut && e
            ? (<g transform={`translate(${e.x.toFixed(1)},${e.y.toFixed(1)})`}><circle r="8" fill={BLUE} /><circle r="2.6" fill="#fff" /></g>)
            : (<><circle cx="160" cy="100" r="8" fill="#d1d5db" /><circle cx="160" cy="100" r="2.6" fill="#fff" /></>)}
          {/* hour numbers */}
          <text x="42" y="104" textAnchor="middle" style={{ font: '600 10px system-ui', fill: '#1a1a2e' }}>20</text>
          <text x="51" y="76" textAnchor="middle" style={{ font: '500 9px system-ui', fill: '#c0c6cc' }}>22</text>
          <text x="75" y="58" textAnchor="middle" style={{ font: '600 10px system-ui', fill: '#1a1a2e' }}>0</text>
          <text x="105" y="58" textAnchor="middle" style={{ font: '500 9px system-ui', fill: '#c0c6cc' }}>2</text>
          <text x="129" y="76" textAnchor="middle" style={{ font: '500 9px system-ui', fill: '#c0c6cc' }}>4</text>
          <text x="138" y="104" textAnchor="middle" style={{ font: '600 10px system-ui', fill: '#1a1a2e' }}>6</text>
          {/* centre */}
          <text x="90" y="82" textAnchor="middle" style={{ font: '700 7px system-ui', letterSpacing: '1px', fill: '#9ca3af' }}>{centerLabel}</text>
          <text x="90" y="100" textAnchor="middle" style={{ font: '600 17px system-ui', letterSpacing: '-.5px', fill: centerColor, fontVariantNumeric: 'tabular-nums' }}>{centerValue}</text>
        </svg>

        <div style={{ flex: 1, minWidth: 0 }}>
          {isClockedIn || isClockedOut ? (
            <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, font: '700 9px system-ui', letterSpacing: '.6px', color: '#9ca3af' }}>
                  <SignIn size={12} weight="fill" color={BLUE} />CLOCK IN
                </div>
                <div style={{ font: '700 19px system-ui', color: '#1a1a2e', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{clockEvent?.clock_in_at ? fmtHM(clockEvent.clock_in_at) : '—'}</div>
                <div style={{ font: '400 11px system-ui', color: '#9ca3af' }}>Tonight</div>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, font: '700 9px system-ui', letterSpacing: '.6px', color: isClockedOut ? '#15803d' : '#9ca3af' }}>
                  <SignOut size={12} weight="fill" color={isClockedOut ? '#16a34a' : '#9ca3af'} />CLOCK OUT
                </div>
                <div style={{ font: '700 19px system-ui', color: isClockedOut ? '#16a34a' : '#9ca3af', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{isClockedOut && clockEvent?.clock_out_at ? fmtHM(clockEvent.clock_out_at) : '06:00'}</div>
                <div style={{ font: '400 11px system-ui', color: '#9ca3af' }}>{isClockedOut ? 'Tonight' : 'Latest'}</div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, font: '700 9px system-ui', letterSpacing: '.6px', color: '#9ca3af' }}>
                  <Moon size={12} weight="fill" color={BLUE} />OPENS
                </div>
                <div style={{ font: '700 19px system-ui', color: '#1a1a2e', marginTop: 1 }}>20:00</div>
                <div style={{ font: '400 11px system-ui', color: '#9ca3af' }}>Tonight</div>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, font: '700 9px system-ui', letterSpacing: '.6px', color: '#9ca3af' }}>
                  <SunHorizon size={12} weight="fill" color="#f59e0b" />CLOSES
                </div>
                <div style={{ font: '700 19px system-ui', color: '#9ca3af', marginTop: 1 }}>06:00</div>
                <div style={{ font: '400 11px system-ui', color: '#9ca3af' }}>Tomorrow</div>
              </div>
            </div>
          )}

          {/* Action button */}
          {isClockedOut ? (
            <button disabled style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', color: '#9ca3af', height: 36, padding: '0 16px', borderRadius: 10, border: '1.5px solid #e5e7eb', font: '700 13px system-ui', cursor: 'default', whiteSpace: 'nowrap' }}>
              <Check size={14} />Done
            </button>
          ) : isClockedIn ? (
            <button onClick={onClockOut} disabled={clockLoading} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'transparent', color: '#e01818', height: 36, padding: '0 16px', borderRadius: 10, border: '1.5px solid #f3c6c6', font: '700 13px system-ui', cursor: clockLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: 10 }}>■</span>{clockLoading ? 'GPS…' : 'Clock out'}
            </button>
          ) : (
            <button onClick={onClockIn} disabled={clockLoading} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#059669', color: '#fff', height: 40, padding: '0 20px', borderRadius: 10, border: 'none', boxShadow: '0 4px 14px rgba(5,150,105,.32)', font: '800 14px system-ui', cursor: clockLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: 11 }}>▶</span>{clockLoading ? 'GPS…' : 'Clock in'}
            </button>
          )}
        </div>
      </div>

      {/* Today's tasks bar */}
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', font: '600 11px system-ui', color: '#9ca3af', marginBottom: 6 }}>
          <span>Today's tasks</span>
          <span><b style={{ color: tasksFull ? '#16a34a' : TEAL }}>{done}/{total}</b> done{tasksFull ? ' ✓' : ''}</span>
        </div>
        <div style={{ height: 9, background: '#eceef0', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ width: `${taskPct}%`, height: '100%', background: tasksFull ? '#16a34a' : TEAL, borderRadius: 6, transition: 'width .4s ease' }} />
        </div>
      </div>

      <style>{`@keyframes ocPulse { 0%,100% { opacity: 1 } 50% { opacity: .55 } }`}</style>
    </header>
  );
}
